import {
  ARDONAGH,
  BRAND_EQX,
  BRAND_EQX_DOMAINS,
  BRAND_EQX_NAME,
  BRAND_NON_PF,
  BRAND_NON_PF_DOMAINS,
  BRAND_NON_PF_NAME,
  BRAND_PF,
  BRAND_PF_DOMAINS,
  BRAND_PF_NAME,
  EQX,
} from './brand.consts';

/**
 * Determines if the current environment is non-production
 * Non-prod: domain contains 'specialty.dev'
 * Prod: domain contains 'priceforbes.com'
 */
export const isNonProduction = (): boolean => {
  if (typeof window === 'undefined') {
    return true; // Default to non-prod for SSR
  }

  const hostname = window.location.hostname;

  return hostname.includes('specialty.dev');
};

/**
 * Determines the brand for non-production environments (*.specialty.dev)
 * - vantage.specialty.dev → vantagePF
 * - eqx-vantage.specialty.dev → vantageEQX
 * - anything else (e.g., asd-vantage.specialty.dev) → vantage
 */
const getBrandForNonProd = (hostname: string): string => {
  const subdomain = hostname.split('.')[0];
  const subDomainPrefix = subdomain.split('-')[0];

  if (subDomainPrefix.toUpperCase().includes(EQX.toUpperCase())) {
    return BRAND_EQX;
  }

  if (subDomainPrefix.toUpperCase().includes(ARDONAGH.toUpperCase())) {
    return BRAND_NON_PF;
  }

  return BRAND_PF;
};

/**
 * Determines the brand for production environments (*.priceforbes.com)
vantage.priceforbes.com - Logo vantagePF
vantage.equinoxunderwriting.com - Logo VantageEQX
vantage.equinoxunderwriting.co.uk - Logo VantageEQX
vantage.ardonaghspecialty.com  - Logo Vantage nonPF
 */

const getBrandForProd = (hostname: string): string => {
  if (BRAND_PF_DOMAINS.includes(hostname)) {
    return BRAND_PF;
  }
  if (BRAND_EQX_DOMAINS.includes(hostname)) {
    return BRAND_EQX;
  }
  if (BRAND_NON_PF_DOMAINS.includes(hostname)) {
    return BRAND_NON_PF;
  }
  return BRAND_NON_PF;
};

export const getBrandForLocal = (): string => {
  // check if ?app=vantagePF or ?app=vantageEQX is in the URL or in local storage
  const urlParams = new URLSearchParams(window.location.search);
  const appParam = urlParams.get('app') ?? '';
  let localStorageApp = localStorage.getItem('app');

  if (appParam !== '' && appParam !== localStorageApp) {
    localStorage.setItem('app', appParam);
    localStorageApp = appParam;
  }

  if (localStorageApp?.toUpperCase().includes(EQX.toUpperCase())) {
    return BRAND_EQX;
  }

  if (localStorageApp?.toUpperCase().includes(ARDONAGH.toUpperCase())) {
    return BRAND_NON_PF;
  }

  return BRAND_PF;
};

/**
 * Determines the brand based on the current URL hostname
 * Checks environment first, then delegates to appropriate brand resolver
 */
export const getBrand = (): string => {
  // Check if window is available (browser environment)
  if (typeof window === 'undefined') {
    return BRAND_NON_PF;
  }

  const hostname = window.location.hostname;
  const isNonProd = isNonProduction();

  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    return getBrandForLocal();
  }

  return isNonProd ? getBrandForNonProd(hostname) : getBrandForProd(hostname);
};

export const getV1Url = (): string => {
  const hostname = window.location.hostname;

  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    return 'http://localhost:8910';
  }

  const subdomain = hostname.split('.')[0].replace('-v1', '');

  const v1Url = `${subdomain}-v1.${hostname.split('.')[1]}.${hostname.split('.')[2]}`;
  return v1Url;
};

export const getV2Url = (): string => {
  const hostname = window.location.hostname;

  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    return 'http://localhost:3000';
  }

  const subdomain = hostname.split('.')[0].replace('-v1', '');

  const v2Url = `${subdomain}.${hostname.split('.')[1]}.${hostname.split('.')[2]}`;
  return `https://${v2Url}`;
};

/**
 * Gets the full application name based on the current brand
 */
export const getAppDetails = (): { name: string; appCode: string } => {
  const brand = getBrand();

  switch (brand) {
    case BRAND_PF:
      return {
        name: BRAND_PF_NAME,
        appCode: BRAND_PF,
      };
    case BRAND_EQX:
      return {
        name: BRAND_EQX_NAME,
        appCode: BRAND_EQX,
      };
    default:
      return {
        name: BRAND_NON_PF_NAME,
        appCode: BRAND_NON_PF,
      };
  }
};

const APP_DETAILS = getAppDetails();

export const APP_NAME = APP_DETAILS.name;

export const APP_CODE = APP_DETAILS.appCode;
